// src/pages/public/EditViewerPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Seo from "../../components/public/Seo";
import { getEditMeta } from "../../state/editsStore";
import { getEditItems } from "../../state/editStore";
import { optimizedImageUrl } from "../../lib/optimizedImageUrl";

function formatDate(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return "—";
  }
}

function pickLabelForPin(pin) {
  if (!pin) return "Saved";
  if (pin.kind === "note") return pin.title || "Note";
  if (pin.kind === "pinned_product")
    return pin.title || (pin.productId ? `Product #${pin.productId}` : "Saved product");
  return pin.title || "Saved image";
}

function NotesChips({ notes }) {
  if (!notes?.length) return null;

  // clamp to ~2 rows
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        marginTop: 12,
        maxHeight: 62,
        overflow: "hidden",
      }}
    >
      {notes.slice(0, 12).map((t, idx) => (
        <span
          key={`notechip_${idx}`}
          className="public-muted"
          style={{
            fontSize: 11,
            padding: "4px 10px",
            borderRadius: 999,
            border: "1px solid rgba(0,0,0,0.10)",
            background: "rgba(0,0,0,0.03)",
            whiteSpace: "nowrap",
          }}
          title={t}
        >
          {t}
        </span>
      ))}
    </div>
  );
}

export default function EditViewerPage() {
  const { editId } = useParams();
  const navigate = useNavigate();

  const [edit, setEdit] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    const meta = getEditMeta(editId);
    setEdit(meta || null);

    const pins = getEditItems(editId) || [];
    setItems(pins);
  }, [editId]);

  const safeId = (edit?.id || editId || "").toString();

  const title = useMemo(() => {
    const n = (edit?.name || "").trim();
    return n || "Untitled edit";
  }, [edit]);

  const subtitle = useMemo(() => {
    const count = items?.length || 0;
    const parts = [];
    parts.push(`${count} ${count === 1 ? "pin" : "pins"}`);
    if (edit?.templateSlug) parts.push("Template");
    return parts.join(" • ");
  }, [items, edit]);

  const noteTexts = useMemo(() => {
    return (items || [])
      .filter((it) => String(it?.kind || "").toLowerCase() === "note")
      .map((it) => String(it?.text || "").trim())
      .filter(Boolean);
  }, [items]);

  const hasPins = items?.length > 0;

  if (!safeId) {
    return (
      <>
        <Seo title="Edit not found — Veya" />
        <p className="public-lede">Edit not found.</p>
        <Link className="public-navlink" to="/edits">
          Back to edits
        </Link>
      </>
    );
  }

  return (
    <>
      <Seo title={`${title} — Veya`} description={subtitle} />

      <div className="public-breadcrumb">
        <Link to="/edits" className="public-navlink">
          Edits
        </Link>
        <span className="public-muted"> / {title}</span>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <button
          className="public-cta"
          onClick={() => {
            window.location.href = `/?openedit=1&editId=${encodeURIComponent(safeId)}`;
          }}
        >
          Open in fitting room
        </button>

        <button className="public-btn" onClick={() => navigate("/edits")}>
          Back to edits
        </button>
      </div>

      {/* Top info card (MoodPage style) */}
      <div className="public-card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontWeight: 900, fontSize: 20, lineHeight: 1.15 }}>{title}</div>

          {edit?.templateSlug ? (
            <span
              className="public-muted"
              style={{
                fontSize: 11,
                padding: "3px 8px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(0,0,0,0.03)",
                whiteSpace: "nowrap",
              }}
              title="Created from a Veya template"
            >
              Template
            </span>
          ) : null}
        </div>

        <div className="public-muted" style={{ marginTop: 6 }}>
          {subtitle}
        </div>

        <div className="public-muted" style={{ marginTop: 10, lineHeight: 1.35 }}>
          Open this edit in the fitting room to keep building — add/remove pins, paste screenshots, and rewrite
          notes.
        </div>

        {/* show note chips as “aesthetic summary” */}
        <NotesChips notes={noteTexts} />

        {/* tiny meta (optional) */}
        {edit ? (
          <div className="public-muted" style={{ fontSize: 12, lineHeight: 1.6, marginTop: 10 }}>
            {"createdAt" in edit ? (
              <div>
                <strong>Created:</strong> {formatDate(edit.createdAt)}
              </div>
            ) : null}
            {"updatedAt" in edit ? (
              <div>
                <strong>Updated:</strong> {formatDate(edit.updatedAt)}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Pins grid (images + notes), no hero */}
      {!hasPins ? (
        <p className="public-muted" style={{ marginTop: 14 }}>
          This edit is empty. Open it in the fitting room to start pinning products, screenshots, and notes.
        </p>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 14,
          }}
        >
          {items.map((pin) => {
            const label = pickLabelForPin(pin);

            if (String(pin?.kind || "").toLowerCase() === "note") {
              return (
                <div key={pin.id} className="public-card" style={{ textAlign: "left", padding: 12 }}>
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 12,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      opacity: 0.65,
                      marginBottom: 8,
                    }}
                  >
                    Note
                  </div>

                  <div style={{ fontWeight: 800, marginBottom: 6 }}>{label}</div>
                  <div className="public-muted" style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                    {pin.text || ""}
                  </div>
                </div>
              );
            }

            // image / pinned product with imageUrl
            return (
              <div key={pin.id} className="public-card" style={{ overflow: "hidden", padding: 0 }}>
                {pin.imageUrl ? (
                  <div style={{ aspectRatio: "4 / 5", background: "rgba(0,0,0,0.06)" }}>
                    <img
  src={optimizedImageUrl(pin.imageUrl, { width: 700, quality: 80 })}
  alt={label}
  loading="lazy"
  decoding="async"
  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
/>
                  </div>
                ) : (
                  <div style={{ padding: 12 }}>
                    <div style={{ fontWeight: 800 }}>{label}</div>
                    <div className="public-muted" style={{ marginTop: 6, fontSize: 12 }}>
                      {String(pin?.kind || "").toLowerCase() === "pinned_product" ? "Saved product" : "Personal pin"}
                    </div>
                  </div>
                )}

                {/* optional footer meta */}
                <div style={{ padding: 12 }}>
                  <div style={{ fontWeight: 800 }}>{label}</div>

                  {pin.sourceUrl ? (
                    <a
                      href={pin.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="public-navlink"
                      style={{ display: "inline-block", marginTop: 6, fontSize: 12 }}
                    >
                      View item
                    </a>
                  ) : (
                    <div className="public-muted" style={{ marginTop: 6, fontSize: 12 }}>
                      {String(pin?.kind || "").toLowerCase() === "pinned_product" ? "Saved product" : "Personal pin"}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}